package com.banyan.flightviewer.web.components.util

/**
 * Unit-aware formatting helpers shared between the aircraft table and the
 * detail panel. Centralised here so unit-toggle logic lives in one place.
 * Mirrors desktop: altitude kept as ft internally; speed kept as knots;
 * distance kept as km; each formatter converts only at display time.
 */

fun formatAltitude(altitude: String, units: String): String {
    val alt = altitude.toDoubleOrNull() ?: return altitude   // e.g. "ground"
    return if (units == "m") "${(alt * 0.3048).toInt().withCommas()} m"
    else "${alt.toInt().withCommas()} ft"
}

/** Short variant for the table (no unit suffix; header carries the unit). */
fun formatAltitudeShort(altitude: String, units: String): String {
    val alt = altitude.toDoubleOrNull() ?: return altitude
    return if (units == "m") (alt * 0.3048).toInt().withCommas()
    else alt.toInt().withCommas()
}

fun formatSpeed(speed: Double, units: String): String = when (units) {
    "kmh", "km/h" -> "${(speed * 1.852).toInt()} km/h"
    "mph" -> "${(speed * 1.15078).toInt()} mph"
    else -> "${speed.toInt()} kts"
}

/** Short variant: just the numeric value; header supplies the unit. */
fun formatSpeedShort(speed: Double, units: String): String = when (units) {
    "kmh", "km/h" -> "${(speed * 1.852).toInt()}"
    "mph" -> "${(speed * 1.15078).toInt()}"
    else -> "${speed.toInt()}"
}

fun formatDistance(distanceKm: Double, units: String): String =
    if (units == "miles") formatOneDecimal(distanceKm * 0.621371)
    else formatOneDecimal(distanceKm)

fun formatVerticalRate(vr: Int): String = when {
    vr > 0 -> "\u2191 ${vr.withCommas()} fpm"
    vr < 0 -> "\u2193 ${(-vr).withCommas()} fpm"
    else -> "0 fpm"
}

/** Compact variant for the table: `↑1200` / `↓800` / `—` */
fun formatVerticalRateShort(vr: Int): String = when {
    vr > 0 -> "\u2191${vr}"
    vr < 0 -> "\u2193${-vr}"
    else -> "\u2014"
}

fun vrClass(vr: Int): String = when {
    vr > 0 -> "up"
    vr < 0 -> "down"
    else -> ""
}

fun formatSquawk(squawk: String): String {
    if (squawk.isBlank()) return "\u2014"
    return when (squawk) {
        "7700" -> "$squawk EMER"
        "7600" -> "$squawk NORAD"
        "7500" -> "$squawk HIJACK"
        else -> squawk
    }
}

fun squawkClass(squawk: String): String =
    if (squawk == "7700" || squawk == "7600" || squawk == "7500") "emer" else ""

/** Header unit suffix helpers (column label) */
fun distanceUnitLabel(units: String): String = if (units == "miles") "mi" else "km"
fun altitudeUnitLabel(units: String): String = if (units == "m") "m" else "ft"
fun speedUnitLabel(units: String): String = when (units) {
    "kmh", "km/h" -> "km/h"
    "mph" -> "mph"
    else -> "kt"
}

fun formatOneDecimal(v: Double): String {
    val rounded = (v * 10).toInt() / 10.0
    return rounded.toString()
}

fun Int.withCommas(): String {
    val s = this.toString()
    val sb = StringBuilder()
    for ((i, c) in s.withIndex()) {
        if (i > 0 && (s.length - i) % 3 == 0) sb.append(',')
        sb.append(c)
    }
    return sb.toString()
}
